"use strict";

/**
 * Project-filter persistence (owner live-use defect 2026-10-06: the
 * top-left project filter in App.jsx reset to "All projects" on every
 * load/reload instead of staying on whatever the site manager last
 * picked). Per browser, via localStorage — guarded with try/catch (private
 * windows, cleared/blocked site data, or any other storage failure must
 * never break the app) and always validated against the server's own
 * project list before use (`pruneSelectedCodes`) so a stale/tampered/
 * foreign value can never select a project the signed-in user does not
 * actually have.
 */
export const SELECTED_CODES_STORAGE_KEY = "sm-cockpit:selectedProjectCodes";

/** The persisted selection, or `[]` on first use or any storage failure. */
export function loadStoredSelectedCodes() {
  try {
    const raw = globalThis.localStorage.getItem(SELECTED_CODES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c) => typeof c === "string" && c) : [];
  } catch {
    return [];
  }
}

/** Best-effort persist; a storage failure is swallowed, never thrown. */
export function storeSelectedCodes(codes) {
  try {
    globalThis.localStorage.setItem(SELECTED_CODES_STORAGE_KEY, JSON.stringify(codes || []));
  } catch {
    // Storage unavailable (private window, quota, blocked) — persistence is
    // a convenience, never a requirement for the screen to work.
  }
}

/**
 * The stored selection, dropped down to codes the server's own project
 * list actually carries. Returns the SAME array reference when nothing
 * changes, so callers can skip a re-render.
 *
 * Deliberately a no-op (returns `codes` unchanged) while `projectCodes` is
 * still empty — the server's project list has not loaded yet on first
 * render, and pruning against an empty list would wipe a just-restored
 * selection before it ever gets the chance to validate.
 */
export function pruneSelectedCodes(codes, projectCodes) {
  if (!codes.length || !projectCodes.length) return codes;
  const pruned = codes.filter((c) => projectCodes.includes(c));
  return pruned.length === codes.length ? codes : pruned;
}
